import type { TaskNode, PlanData } from "../shared/types";
import { normalize } from "../shared/model";
import { resyncEstimateTotals } from "../shared/mutations";

// Single mutable app-state object. Pure logic never touches it; the view reads
// `state` and passes the needed fields into the (tested) shared functions.
export interface AppState {
  // persisted plan
  tree: TaskNode[];
  projectName: string;
  showTags: boolean;
  showVersions: boolean;
  showComponents: boolean;
  showDone: boolean;
  showJira: boolean;
  showGantt: boolean;
  jiraUrl: string;
  jiraDelim: string;
  countDeps: boolean;
  depDocOrder: boolean;
  showComplexity: boolean;
  estimations: string[];
  estimateView: string;
  defaultEstimate: number;
  versions: string[];
  components: string[];
  changes: number;
  lastModified: string | null;
  // persistence mode
  mode: "client" | "server";
  guid: string | null;
  serverVersion: number;
  // view / ephemeral
  view: "tree" | "version";
  showPinnedOnly: boolean; // "Pinned only" tree filter (ephemeral)
  versionFilter: string; // by-version tree filter ("" = all; ephemeral)
  hideDone: boolean; // hide completed tasks/subtrees from the tree (ephemeral)
  activeTag: string | null;
  pendingFocus: { id: string; field: "name" | "est"; select: boolean; stream?: string } | null;
  flashId: string | null; // task to flash on the next render, so a move is easy to follow (ephemeral)
  // The task just created and focused, still carrying its placeholder name. Naming it is part of
  // creating it, so Enter there chains the next task; Enter on any *other* renamed task only saves.
  // Cleared as soon as that input loses focus (ephemeral).
  freshId: string | null;
  visibleOrder: string[];
  rendering: boolean;
}

export const state: AppState = {
  tree: [],
  projectName: "Mini Project Planner",
  showTags: true,
  showVersions: true,
  showComponents: true,
  showDone: true,
  showJira: true,
  showGantt: true,
  jiraUrl: "",
  jiraDelim: ">",
  countDeps: false,
  depDocOrder: false,
  showComplexity: true,
  estimations: [],
  estimateView: "all",
  defaultEstimate: 1,
  versions: [],
  components: [],
  changes: 0,
  lastModified: null,
  mode: "client",
  guid: null,
  serverVersion: 0,
  view: "tree",
  showPinnedOnly: false,
  versionFilter: "",
  hideDone: false,
  activeTag: null,
  pendingFocus: null,
  flashId: null,
  freshId: null,
  visibleOrder: [],
  rendering: false,
};

export function snapshot(): PlanData {
  const s = state;
  return {
    name: s.projectName,
    showTags: s.showTags,
    showVersions: s.showVersions,
    showComponents: s.showComponents,
    showDone: s.showDone,
    showJira: s.showJira,
    showGantt: s.showGantt,
    jiraUrl: s.jiraUrl,
    jiraDelim: s.jiraDelim,
    countDeps: s.countDeps,
    depDocOrder: s.depDocOrder,
    showComplexity: s.showComplexity,
    estimations: s.estimations,
    estimateView: s.estimateView,
    defaultEstimate: s.defaultEstimate,
    versions: s.versions,
    components: s.components,
    changes: s.changes,
    lastModified: s.lastModified,
    tree: s.tree,
  };
}

export function applyData(data: any): void {
  state.tree = normalize((data && data.tree) || []);
  if (data && typeof data.name === "string" && data.name) state.projectName = data.name;
  if (data && typeof data.showTags === "boolean") state.showTags = data.showTags;
  if (data && typeof data.showVersions === "boolean") state.showVersions = data.showVersions;
  if (data && typeof data.showComponents === "boolean") state.showComponents = data.showComponents;
  if (data && typeof data.showDone === "boolean") state.showDone = data.showDone;
  if (data && typeof data.showJira === "boolean") state.showJira = data.showJira;
  state.showGantt = data && typeof data.showGantt === "boolean" ? data.showGantt : true; // default on (backward compat)
  if (data && typeof data.jiraUrl === "string") state.jiraUrl = data.jiraUrl;
  if (data && typeof data.jiraDelim === "string" && data.jiraDelim.trim()) state.jiraDelim = data.jiraDelim;
  if (data && typeof data.countDeps === "boolean") state.countDeps = data.countDeps;
  if (data && typeof data.depDocOrder === "boolean") state.depDocOrder = data.depDocOrder;
  state.showComplexity = data && typeof data.showComplexity === "boolean" ? data.showComplexity : true; // default on (backward compat)
  if (data && Array.isArray(data.estimations)) state.estimations = data.estimations.filter((v: any) => typeof v === "string" && v);
  else state.estimations = [];
  state.estimateView = data && typeof data.estimateView === "string" ? data.estimateView : "all";
  state.defaultEstimate = data && Number.isFinite(data.defaultEstimate) && data.defaultEstimate >= 0 ? data.defaultEstimate : 1;
  if (data && Array.isArray(data.versions)) state.versions = data.versions.filter((v: any) => typeof v === "string" && v);
  if (data && Array.isArray(data.components)) state.components = data.components.filter((v: any) => typeof v === "string" && v);
  if (data && Number.isFinite(data.changes)) state.changes = data.changes;
  if (data && typeof data.lastModified === "string") state.lastModified = data.lastModified;
  resyncEstimateTotals(state.tree, state.estimations); // keep estimate == sum(streams) after load
}
